import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { HOST } from 'config/config';
import { BlobStorageTypeEnumValue } from 'enums/BlobStorageTypeEnum';
import useToast from 'services/Hooks/useToast';
import tableDataQueryStringBuilder from 'services/Utilities/TableData';

export const usePrefetchExteriorSignageQuote = () => {
  const queryClient = useQueryClient();
  queryClient.prefetchQuery({
    queryKey: ['exteriorSignageQuote'],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/exterior-signage-quotes/active');
      return response.data;
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

export const useExteriorSignageQuotes = tableDataParam => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['exteriorSignageQuotes', JSON.stringify(tableDataParam)],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/exterior-signage-quotes', {
        paramsSerializer: params => tableDataQueryStringBuilder(params),
        params: tableDataParam,
      });
      return response.data;
    },
    throwOnError: errorMessage,
    staleTime: 0,
  });
  return query;
};

export const useActiveExteriorSignageQuote = isLogged => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['exteriorSignageQuote'],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/exterior-signage-quotes/active');
      return response.data;
    },
    throwOnError: errorMessage,
    enabled: !!isLogged,
  });
  return query;
};

export const useExteriorSignageQuote = exteriorSignageQuoteId => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['historyExteriorSignageQuote', exteriorSignageQuoteId],
    queryFn: async () => {
      if (exteriorSignageQuoteId) {
        const response = await axios.get(`${HOST}/api/dealer-portal/exterior-signage-quotes/${exteriorSignageQuoteId}`);
        return response.data;
      } else {
        return null;
      }
    },
    throwOnError: errorMessage,
  });
  return query;
};

export const usePostExteriorSignageQuoteItem = isDemo => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addExteriorSignageQuoteItem'],
    mutationFn: async exteriorSignageQuoteItem => {
      return await axios.post(
        `${HOST}/api/dealer-portal/exterior-signage-quotes/items`,
        isDemo ? { ...exteriorSignageQuoteItem, isDemo } : exteriorSignageQuoteItem,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostExteriorSignageQuoteCheckout = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['addExteriorSignageQuoteItem'],
    mutationFn: async exteriorSignageQuote => {
      return await axios.post(`${HOST}/api/dealer-portal/exterior-signage-quotes/${exteriorSignageQuote.id}/checkout`);
    },
    onSuccess: () => {
      saveMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], null);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostChangeExteriorSignageQuoteItemQuantity = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['changeQuantityExteriorSignageQuoteItem'],
    mutationFn: async exteriorSignageQuoteItem => {
      return await axios.post(
        `${HOST}/api/dealer-portal/exterior-signage-quotes/items/${exteriorSignageQuoteItem.id}/quantity`,
        exteriorSignageQuoteItem,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchExteriorSignageQuoteChangeShipping = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['exteriorSignageQuoteChangeShipping'],
    mutationFn: async exteriorSignageQuote => {
      return await axios.patch(
        `${HOST}/api/dealer-portal/exterior-signage-quotes/${exteriorSignageQuote.id}/shipping`,
        exteriorSignageQuote,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchExteriorSignageQuoteChangeInformation = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['exteriorSignageQuoteChangeInformation'],
    mutationFn: async exteriorSignageQuote => {
      return await axios.patch(
        `${HOST}/api/dealer-portal/exterior-signage-quotes/${exteriorSignageQuote.id}/information`,
        exteriorSignageQuote,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const usePatchApproveExteriorSignageQuote = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['approveExteriorSignageQuote'],
    mutationFn: async exteriorSignageQuote => {
      return await axios.patch(
        `${HOST}/api/dealer-portal/exterior-signage-quotes/${exteriorSignageQuote.id}/status`,
        exteriorSignageQuote,
      );
    },
    onSuccess: ({ data }) => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuotes'] });
      queryClient.setQueryData(['historyExteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteExteriorSignageQuoteItem = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteExteriorSignageQuoteItem'],
    mutationFn: async exteriorSignageQuoteItem => {
      return await axios.delete(`${HOST}/api/dealer-portal/exterior-signage-quotes/items/${exteriorSignageQuoteItem.id}`);
    },
    onSuccess: ({ data }) => {
      deleteMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], data);
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteExteriorSignageQuote = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteExteriorSignageQuote'],
    mutationFn: async exteriorSignageQuote => {
      return await axios.delete(`${HOST}/api/dealer-portal/exterior-signage-quotes/${exteriorSignageQuote.id}`);
    },
    onSuccess: () => {
      deleteMessage();
      queryClient.setQueryData(['exteriorSignageQuote'], null);
    },
    onError: errorMessage,
  });
  return query;
};

export const useUpdateExteriorSignageQuoteLogo = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['updateVendorPhoto'],
    mutationFn: async exteriorSignageQuoteInfo => {
      var formData = new FormData();
      exteriorSignageQuoteInfo.files.forEach(f => formData.append(`files`, f));

      formData.append('type', BlobStorageTypeEnumValue.ExteriorSignageQuote);

      return await axios.post(
        `${HOST}/api/dashboard/attached-files/${exteriorSignageQuoteInfo.exteriorSignageQuoteId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
    },
    onSuccess: () => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuote'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteExteriorSignageQuoteLogo = () => {
  const { deleteMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteVendorPhoto'],
    mutationFn: async ({ exteriorSignageQuoteId, photoId }) => {
      var formData = new FormData();
      formData.append('type', BlobStorageTypeEnumValue.ExteriorSignageQuote);
      return await axios.delete(
        `${HOST}/api/dashboard/attached-files/${exteriorSignageQuoteId}/${photoId}?type=${BlobStorageTypeEnumValue.ExteriorSignageQuote}`,
      );
    },
    onSuccess: () => {
      deleteMessage();
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuote'] });
    },
    onError: errorMessage,
  });
  return query;
};
